import { type ReactElement } from 'react';

/**
 * One line icon per screen of the sidebar (24×24, stroke = currentColor), drawn here rather than pulled from an icon
 * package: two dozen glyphs do not justify a dependency. Keyed by route, so `nav.ts` stays the single list of screens.
 */
const PATHS: Readonly<Record<string, readonly string[]>> = {
  '/dashboard': ['M4 13h6V4H4z', 'M14 20h6v-9h-6z', 'M4 20h6v-3H4z', 'M14 4v3h6V4z'],
  '/store': [
    'M4 9l1.5-5h13L20 9',
    'M4 9h16v2a3 3 0 0 1-5.33 1.9A3 3 0 0 1 12 14a3 3 0 0 1-2.67-1.1A3 3 0 0 1 4 11z',
    'M5 14v6h14v-6',
    'M10 20v-4h4v4',
  ],
  '/todo': ['M9 6h11', 'M9 12h11', 'M9 18h11', 'M4 6l1 1 2-2', 'M4 12l1 1 2-2', 'M4 18l1 1 2-2'],
  '/orders': [
    'M3 4h2l2.4 11.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.76L20 8H6.2',
    'M9 20h.01',
    'M17 20h.01',
  ],
  '/sales': [
    'M12 3v18',
    'M17 7.5C17 5.6 14.8 4.5 12 4.5S7 5.6 7 7.6c0 4.6 10 2.3 10 7 0 2-2.2 3-5 3s-5-1.1-5-3',
  ],
  '/products': ['M12 3l8 4.5v9L12 21l-8-4.5v-9z', 'M12 12l8-4.5', 'M12 12v9', 'M12 12L4 7.5'],
  '/inventory': ['M4 7h16v13H4z', 'M3 4h18v3H3z', 'M10 11h4'],
  '/customers': [
    'M16 20v-1.5A3.5 3.5 0 0 0 12.5 15h-5A3.5 3.5 0 0 0 4 18.5V20',
    'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4',
    'M15.5 4.2a3.5 3.5 0 0 1 0 6.6',
  ],
  '/marketing': ['M4 10v4h3l6 4V6L7 10z', 'M17 9a4 4 0 0 1 0 6', 'M7 14l1 5h2.5l-1-5'],
  '/analytics': ['M4 20V10', 'M10 20V4', 'M16 20v-7', 'M22 20H2'],
  '/delivery': [
    'M3 6h11v10H3z',
    'M14 10h4l3 3v3h-7z',
    'M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  ],
  '/payments': ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h3'],
  '/invoices': ['M6 3h9l4 4v14H6z', 'M14 3v5h5', 'M9 13h7', 'M9 17h5'],
  '/categories': ['M4 4h7v7H4z', 'M13 4h7v7h-7z', 'M4 13h7v7H4z', 'M13 13h7v7h-7z'],
  '/promotions': ['M20 12l-8 8-9-9V3h8z', 'M7.5 7.5h.01'],
  '/delivery/account': ['M12 3l8 3v6c0 4.4-3.4 8.2-8 9-4.6-.8-8-4.6-8-9V6z', 'M9 12l2 2 4-4'],
  '/team': [
    'M17 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-3A3.5 3.5 0 0 0 7 18.5V20',
    'M12 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M5 20v-1a3 3 0 0 1 1.5-2.6',
    'M19 20v-1a3 3 0 0 0-1.5-2.6',
  ],
  '/settings': [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  ],
  '/account': ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1'],
};

const FALLBACK = ['M12 12h.01'];

export function NavIcon({
  to,
  className = 'h-[18px] w-[18px]',
}: {
  to: string;
  className?: string;
}): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`shrink-0 ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {(PATHS[to] ?? FALLBACK).map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
